// /partner/usage — Calls & texts: AI receptionist minutes, texts, top-ups, automatic top-up, phone numbers.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, MessageSquare, Plus, Search, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Card, Stat, Empty, Loading, inp, gbp, act, useLoad } from "@/components/kit";

const label = { minutes: "Receptionist minutes", texts: "Texts" };

function Meter({ k, u }) {
  const pct = u.allowance ? Math.min(100, Math.round((u.used / u.allowance) * 100)) : 0;
  return (
    <Card title={label[k]} right={k === "minutes" ? <Phone size={18} /> : <MessageSquare size={18} />}>
      <p className="text-3xl font-bold" data-testid={"usage-remaining-" + k}>{Math.floor(u.remaining)}<span className="ml-1 text-sm font-medium text-muted-foreground">left</span></p>
      {u.allowance ? (
        <>
          <div className="mt-3 h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-primary" style={{ width: pct + "%" }} /></div>
          <p className="mt-2 text-xs text-muted-foreground">{Math.floor(u.used)} of {u.allowance} this month from your plan · resets on the 1st</p>
        </>
      ) : <p className="mt-2 text-xs text-muted-foreground">Not included in your plan — top-ups below.</p>}
      {u.prepaid ? <p className="mt-1 text-xs text-muted-foreground">{Math.floor(u.prepaid)} from top-ups (never expire)</p> : null}
    </Card>
  );
}

function Requests() {
  const [r, reload] = useLoad("/phone/requests");
  if (!r || (!(r.requests || []).length && !(r.voicemails || []).length)) return null;
  const mark = async (id, status) => { if (await act("/phone/requests/" + id, "PATCH", { status }, "Updated")) reload(); };
  return (
    <Card title="Taken by your receptionist">
      <div className="space-y-2">
        {(r.requests || []).map((q) => (
          <div key={q.id} className="flex flex-wrap items-center gap-2 rounded-xl border p-3 text-sm">
            <span className="flex-1"><b>{q.name || "Caller"}</b> · {q.service || "booking"} · {q.when || "time to confirm"} <span className="text-muted-foreground">{q.caller || ""}</span></span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{q.status}</span>
            {q.status === "new" ? <><Button size="sm" variant="outline" className="rounded-full" onClick={() => mark(q.id, "booked")}>Booked</Button><Button size="sm" variant="ghost" className="rounded-full" onClick={() => mark(q.id, "called_back")}>Called back</Button></> : null}
          </div>
        ))}
        {(r.voicemails || []).map((v) => <p key={v.call_sid} className="text-sm">Voicemail from {v.from || "a caller"} — <a className="underline" href={v.voicemail_url} target="_blank" rel="noreferrer">listen</a></p>)}
      </div>
    </Card>
  );
}

export default function PartnerUsage() {
  const [d, reload] = useLoad("/usage");
  const [ph, reloadPh] = useLoad("/phone");
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("local");
  const [found, setFound] = useState(null);
  const [comp, setComp] = useState({ business_name: "", contact_name: "", email: "", street: "", city: "", postcode: "" });
  const [st, setSt] = useState({ forward_to: "", greeting: "", sender_name: "" });

  useEffect(() => {
    const sid = params.get("topup");
    if (sid) api("/usage/topup/status/" + encodeURIComponent(sid)).then(() => reload()).catch(() => {});
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (ph && ph.settings) setSt((s) => ({ ...s, ...ph.settings })); }, [ph]);

  if (d === undefined) return <Loading />;
  if (!d) return <Empty>Calls and texts are part of partner plans. Choose a plan first.</Empty>;

  const buy = async (pack) => { const r = await act("/usage/topup", "POST", { pack, return_base: window.location.origin }); if (r && r.url) window.location.assign(r.url); };
  const addon = async () => { const r = await act("/usage/receptionist-addon", "POST", { return_base: window.location.origin }); if (r && r.url) window.location.assign(r.url); };
  const auto = async (k, on, pack) => { if (await act("/usage/auto", "PATCH", { kind: k, on, pack, threshold: k === "minutes" ? 20 : 50, monthly_cap: 50 }, on ? "Automatic top-up on" : "Automatic top-up off")) reload(); };
  const search = async () => { const r = await act("/phone/numbers/search?contains=" + encodeURIComponent(q) + "&kind=" + kind, "GET"); if (r) setFound(r.numbers || []); };
  const pick = async (n) => { if (await act("/phone/numbers/buy", "POST", { phone_number: n }, "Number added")) { setFound(null); reloadPh(); } };
  const sendComp = async (e) => { e.preventDefault(); if (await act("/phone/compliance", "POST", comp, "Details sent — we'll tell you when they're approved")) reloadPh(); };
  const saveSt = async (e) => { e.preventDefault(); const body = {}; Object.entries(st).forEach(([k, v]) => { if (v || v === false) body[k] = v; }); delete body.updated_at; delete body.user_id; await act("/phone/settings", "PATCH", body, "Saved"); };

  return (
    <div className="space-y-5" data-testid="partner-usage">
      <div><h1 className="text-2xl font-bold">Calls & texts</h1><p className="text-sm text-muted-foreground">Your AI receptionist minutes, texts and phone numbers. Nothing is ever charged without the credit being there first.</p></div>
      <div className="grid gap-4 sm:grid-cols-2">{["minutes", "texts"].map((k) => <Meter key={k} k={k} u={d.usage[k]} />)}</div>

      {d.addon.available && !d.addon.active ? (
        <Card title="AI receptionist add-on">
          <p className="text-sm">{d.addon.minutes} minutes a month and a phone number for {gbp(d.addon.price)}/month. Answers calls 24/7 and takes booking details.</p>
          <Button className="mt-3 rounded-full" onClick={addon} data-testid="usage-addon">Add for {gbp(d.addon.price)}/mo</Button>
        </Card>
      ) : null}

      <Card title="Top-ups" right={<Plus size={18} />}>
        <div className="grid gap-3 sm:grid-cols-3">
          {d.topups.map((t) => (
            <div key={t.id} className="rounded-xl border p-4">
              <p className="font-semibold">{t.label}</p><p className="text-2xl font-bold">{gbp(t.price)}</p>
              <Button size="sm" className="mt-2 rounded-full" onClick={() => buy(t.id)} data-testid={"usage-buy-" + t.id}>Buy</Button>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2 text-sm">
          {["minutes", "texts"].map((k) => {
            const a = (d.auto || {})[k] || {};
            const pack = k === "minutes" ? "minutes_100" : "texts_100";
            return (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" checked={!!a.on} disabled={!d.card_saved} onChange={(e) => auto(k, e.target.checked, pack)} data-testid={"usage-auto-" + k} />
                Top up {label[k].toLowerCase()} automatically when they run low (up to £50 a month)
              </label>
            );
          })}
          {!d.card_saved ? <p className="text-xs text-muted-foreground">Buy one top-up first — that saves your card for automatic top-ups.</p> : null}
        </div>
      </Card>

      <Card title="Phone numbers" right={<Phone size={18} />}>
        {ph === undefined ? <Loading /> : !ph || !ph.configured ? <Empty>Phone numbers are switching on soon.</Empty> : (
          <div className="space-y-4">
            {(ph.numbers || []).map((n) => <p key={n.id} className="font-mono-num text-lg font-semibold">{n.phone_number}</p>)}
            {!ph.allowed ? <Empty>Numbers come with Scale, Scale+ or the receptionist add-on.</Empty> : (ph.numbers || []).length >= ph.allowed ? null : ph.compliance && ph.compliance.status === "twilio-approved" ? (
              <div>
                <div className="flex gap-2">
                  <input className={inp} placeholder="Digits you'd like, e.g. 0114 or 777" value={q} onChange={(e) => setQ(e.target.value)} data-testid="usage-number-q" />
                  <select className={inp + " max-w-[8rem]"} value={kind} onChange={(e) => setKind(e.target.value)}><option value="local">Local</option><option value="mobile">Mobile</option></select>
                  <Button onClick={search} className="rounded-full"><Search size={16} /></Button>
                </div>
                {found ? (found.length ? <div className="mt-3 grid gap-2 sm:grid-cols-3">{found.map((n) => (
                  <button key={n.phone_number} className="rounded-xl border p-3 text-left hover:border-primary" onClick={() => pick(n.phone_number)}>
                    <p className="font-semibold">{n.friendly}</p><p className="text-xs text-muted-foreground">{n.locality || n.region || ""}</p>
                  </button>))}</div> : <Empty>No numbers match — try fewer digits.</Empty>) : null}
              </div>
            ) : (
              <form onSubmit={sendComp} className="grid gap-2 sm:grid-cols-2">
                <p className="sm:col-span-2 flex items-center gap-2 text-sm"><ShieldCheck size={16} /> UK numbers need your business details on file with the phone network first.{ph.compliance ? " Status: " + ph.compliance.status : ""}</p>
                {[["business_name", "Business name"], ["contact_name", "Your name"], ["email", "Email"], ["street", "Street address"], ["city", "Town or city"], ["postcode", "Postcode"]].map(([k, l]) => (
                  <input key={k} className={inp} placeholder={l} value={comp[k]} onChange={(e) => setComp({ ...comp, [k]: e.target.value })} required />
                ))}
                <Button type="submit" className="rounded-full sm:col-span-2">Send details</Button>
              </form>
            )}
            <form onSubmit={saveSt} className="grid gap-2 sm:grid-cols-3">
              <input className={inp} placeholder="Forward calls to (+44…)" value={st.forward_to || ""} onChange={(e) => setSt({ ...st, forward_to: e.target.value })} />
              <input className={inp} placeholder="Greeting" value={st.greeting || ""} onChange={(e) => setSt({ ...st, greeting: e.target.value })} />
              <input className={inp} placeholder="Text sender name (3–11 letters)" value={st.sender_name || ""} onChange={(e) => setSt({ ...st, sender_name: e.target.value })} />
              <label className="flex items-center gap-2 text-sm sm:col-span-3"><input type="checkbox" checked={st.receptionist_on !== false} onChange={(e) => setSt({ ...st, receptionist_on: e.target.checked })} /> AI receptionist answers calls (when it's on your plan)</label>
              <Button type="submit" variant="outline" className="rounded-full sm:col-span-3">Save call & text settings</Button>
            </form>
            <p className="text-xs text-muted-foreground">Keeping your own number? {ph.forwarding_help}</p>
          </div>
        )}
      </Card>
      <Requests />
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat k="Longest call" v={d.limits.max_call_minutes + " min"} />
        <Stat k="Texts per customer a day" v={d.limits.texts_per_customer_per_day} />
        <Stat k="No marketing texts" v={d.limits.marketing_quiet_hours} />
      </div>
    </div>
  );
}
